var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import { RewardForValue } from 'root/src/shared/constants/dropEvent';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { ModalTitle } from 'root/src/client/v2/common/components/modal/ModalTitle';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { UpdatePoolActionType } from 'root/src/shared/giveaways/giveaways.constants';
import toast from 'react-hot-toast';
import { useCreatorDataApi } from 'root/src/client/logic/api/hooks/useCreatorDataApi';
import { NumericStepper } from 'root/src/client/v2/common/components/form/NumericStepper';
import { AdvancedSettingsBox, AdvancedSettingsField, } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/AdvancedSettingsSection';
export const UpdatePoolButton = ({ rewardFor, rewardId, available, }) => {
    const isRemoveAction = rewardFor === RewardForValue.FANS;
    const modal = useModal();
    const onClick = () => {
        modal.show(<UpdatePoolConfirmationModal actionType={isRemoveAction
                ? UpdatePoolActionType.Remove
                : UpdatePoolActionType.Add} rewardId={rewardId} available={available}/>);
    };
    return (<div className="absolute z-10 bottom-3 right-3 w-[40px] h-[40px]">
			<BaseTooltip asChild tooltip={(<BaseTooltipContent className="py-1 my-1 bg-pink text-dark-3 font-bold">
						{isRemoveAction
                ? 'Remove reward from the Giveaway Loop'
                : 'Add reward to the Giveaway Loop'}
					</BaseTooltipContent>)}>
				<ButtonNew variant={ButtonNewVariant.Error} className="flex justify-center items-center rounded-full w-11 h-11" onClick={onClick}>
					{isRemoveAction ? <DeleteIcon /> : <AddIcon />}
				</ButtonNew>
			</BaseTooltip>
		</div>);
};
const UpdatePoolConfirmationModal = (props) => {
    const { onSubmit, isLoading, amount, setAmount } = useUpdatePoolConfirmationModal(props);
    const modal = useModal();
    const isSingleReward = props.available === 1;
    const rewardWord = isSingleReward ? 'Reward' : 'Rewards';
    return (<ModalLayout>
			<div className="flex flex-col justify-center items-center gap-4 pb-4">
				<ModalTitle>
					{props.actionType === UpdatePoolActionType.Add
            ? `Add a ${rewardWord}`
            : `Remove a ${rewardWord}`}
				</ModalTitle>
				<p className="text-center text-light-5">
					{props.actionType === UpdatePoolActionType.Add
            ? `Do you want to add a ${rewardWord.toLowerCase()} to the Giveaway Loop?`
            : `Do you want to remove a ${rewardWord.toLowerCase()} from the Giveaway Loop?`}
				</p>
				{!isSingleReward && (<RewardsAmountField value={amount} onChange={setAmount} available={props.available} actionType={props.actionType}/>)}
				<div className="flex justify-center items-center gap-6">
					<ButtonNew disabled={isLoading} onClick={modal.hide} variant={ButtonNewVariant.Error} className="font-bold uppercase" type="button">
						Cancel
					</ButtonNew>
					<ButtonNew disabled={isLoading} onClick={onSubmit} className="font-bold uppercase w-32" type="button">
						{isLoading ? <LoadingCircles width={20} height={20}/> : 'Confirm'}
					</ButtonNew>
				</div>
			</div>
		</ModalLayout>);
};
const RewardsAmountField = ({ available, value, onChange, actionType }) => {
    const helperText = actionType === UpdatePoolActionType.Add
        ? 'How many rewards to add?'
        : 'How many rewards to remove?';
    return (<AdvancedSettingsBox>
			<AdvancedSettingsField id="amount" label="Amount" helperText={helperText}>
				<NumericStepper min={1} max={available} value={value} onChange={onChange}/>
			</AdvancedSettingsField>
		</AdvancedSettingsBox>);
};
const useUpdatePoolConfirmationModal = ({ rewardId, actionType, available, }) => {
    const updatePoolMutation = trpc.giveaways.updateGiveawayRewardsPool.useMutation();
    const modal = useModal();
    const [isLoading, setLoading] = useState(false);
    const { fetchRewards } = useCreatorDataApi();
    const [amount, setAmount] = useState(available);
    const onSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            yield updatePoolMutation.mutateAsync({
                rewardId,
                actionType,
                amount,
            });
            yield fetchRewards();
            setLoading(false);
            const isPlural = amount > 1;
            const message = actionType === UpdatePoolActionType.Add
                ? (isPlural ? `${amount} rewards successfully added!` : 'Reward successfully added!')
                : (isPlural ? `${amount} rewards successfully removed!` : 'Reward successfully removed!');
            toast.success(message);
        }
        catch (e) {
            const message = e.message || 'Something went wrong';
            toast.error(message);
            setLoading(false);
        }
        modal.hide();
    });
    return { onSubmit, isLoading: updatePoolMutation.isPending || isLoading, amount, setAmount };
};
